/**
 * Seasonal theme presets — the single source of truth for the design-token
 * overrides + decorative art direction each approved template applies.
 *
 * A theme = a cohesive set of CSS-variable overrides (HSL triplet strings that
 * match the base tokens in `index.css`), plus decorative metadata used by
 * <SeasonalAccents/> to paint an immersive, premium, portal-wide atmosphere:
 *   • intensity : 'strong' (atmospheric, tinted sidebar/background) or
 *                 'subtle' (refined accents, neutral base kept legible)
 *   • ambient   : the ambient overlay treatment (gold-night | rays | mosaic |
 *                 beams | lights | sun | frost | none)
 *   • flags     : country ISO codes rendered as an elegant heraldic strip
 *   • accent_effect : the falling / twinkling particle layer
 *   • hero      : premium banner image for Strong themes
 *
 * The ThemeProvider writes the token overrides onto `document.documentElement`,
 * so the whole portal re-skins from one place — there is no parallel styling
 * system. Token keys map 1:1 to the real variables in index.css.
 */

export const TEMPLATE_ORDER = [
  // Strong holiday themes
  'new_year', 'easter', 'christmas', 'hispanic_heritage', 'central_america',
  // Cultural & national celebrations (subtle)
  'three_kings', 'valentines', 'dr_independence', 'mothers_day', 'fathers_day',
  'argentina_independence', 'colombia_independence', 'mexico_independence',
  'chile_independence', 'puerto_rico',
  // Base seasons
  'spring', 'summer', 'fall', 'winter',
  // Retained + custom
  'thanksgiving', 'custom',
];

const HERO_BASE = 'https://static.prod-images.emergentagent.com/jobs/24da4b5c-f7b8-4285-a82c-9685a2a83611/images';

export const SEASONAL_PRESETS = {
  // ─── STRONG THEMES ──────────────────────────────────────────────────
  new_year: {
    label: 'New Year',
    intensity: 'strong',
    accent_effect: 'sparkle',
    ambient: 'gold-night',
    swatch: '#c79a2e',
    hero: `${HERO_BASE}/1d03301aa3e012bb19d2deed03b1ec382f0d5793fdaa821a8d3c4aa44300340e.jpeg`,
    tokens: {
      brand: '45 90% 48%', 'brand-light': '46 92% 60%', 'brand-dark': '40 85% 40%',
      primary: '222 47% 16%', 'primary-foreground': '43 85% 92%',
      accent: '44 62% 92%', 'accent-foreground': '222 47% 22%',
      ring: '45 90% 48%', background: '222 40% 98%',
      'sidebar-bg': '222 47% 10%', 'sidebar-active': '46 92% 60%',
    },
  },
  easter: {
    label: 'Holy Week / Easter',
    intensity: 'strong',
    accent_effect: 'none',
    ambient: 'rays',
    swatch: '#cfa64a',
    hero: `${HERO_BASE}/891227088dbc04f0b9c1a1c32c9b01f50361aa41e71fbc58341e218d53953b26.jpeg`,
    tokens: {
      brand: '38 88% 46%', 'brand-light': '40 90% 58%', 'brand-dark': '34 80% 40%',
      primary: '260 35% 42%', 'primary-foreground': '40 60% 97%',
      accent: '40 56% 92%', 'accent-foreground': '260 40% 30%',
      ring: '38 88% 46%', background: '40 42% 98%',
      'sidebar-bg': '260 26% 16%', 'sidebar-active': '38 88% 58%',
    },
  },
  christmas: {
    label: 'Christmas',
    intensity: 'strong',
    accent_effect: 'snow',
    ambient: 'lights',
    swatch: '#b11d2e',
    hero: `${HERO_BASE}/a8d63ae1b3ea1410fc90dc6e0c9b90aedd23667f268619f14c6bde4fe4171a9b.jpeg`,
    tokens: {
      brand: '150 55% 32%', 'brand-light': '150 50% 44%', 'brand-dark': '152 60% 24%',
      primary: '355 72% 44%', 'primary-foreground': '0 0% 100%',
      accent: '43 85% 92%', 'accent-foreground': '355 70% 32%',
      ring: '355 72% 44%', background: '150 22% 98%',
      'sidebar-bg': '150 40% 11%', 'sidebar-active': '43 88% 58%',
    },
  },
  hispanic_heritage: {
    label: 'Hispanic Heritage',
    intensity: 'strong',
    accent_effect: 'none',
    ambient: 'mosaic',
    flags: ['MX', 'CO', 'AR', 'CL', 'PR', 'DR', 'GT', 'VE', 'PE', 'EC'],
    swatch: '#d2622a',
    hero: `${HERO_BASE}/922767dcdaf95a747c6bb20460f31bb766665076dacb5be875b47e8fdc98418c.jpeg`,
    tokens: {
      brand: '18 85% 50%', 'brand-light': '22 88% 60%', 'brand-dark': '14 78% 42%',
      primary: '18 80% 42%', 'primary-foreground': '0 0% 100%',
      accent: '38 80% 92%', 'accent-foreground': '18 80% 30%',
      ring: '18 85% 50%', background: '25 45% 98%',
      'sidebar-bg': '18 35% 15%', 'sidebar-active': '38 88% 58%',
    },
  },
  central_america: {
    label: 'Central America',
    intensity: 'strong',
    accent_effect: 'none',
    ambient: 'beams',
    flags: ['GT', 'SV', 'HN', 'NI', 'CR'],
    swatch: '#1e73c4',
    hero: `${HERO_BASE}/c581df346703acc761e7fbdaea0135f6cd072ea04135d6fc054b450f295cd2b8.jpeg`,
    tokens: {
      brand: '210 85% 46%', 'brand-light': '210 88% 58%', 'brand-dark': '212 80% 38%',
      primary: '210 85% 42%', 'primary-foreground': '0 0% 100%',
      accent: '210 70% 93%', 'accent-foreground': '210 85% 28%',
      ring: '210 85% 46%', background: '210 50% 98%',
      'sidebar-bg': '210 45% 13%', 'sidebar-active': '210 88% 62%',
    },
  },

  // ─── CULTURAL & NATIONAL CELEBRATIONS (subtle) ──────────────────────
  three_kings: {
    label: 'Three Kings Day',
    intensity: 'subtle',
    accent_effect: 'sparkle',
    ambient: 'gold-night',
    swatch: '#6b3fa0',
    tokens: {
      brand: '268 55% 45%', 'brand-light': '270 60% 58%', 'brand-dark': '266 55% 36%',
      primary: '268 50% 30%', 'primary-foreground': '45 85% 92%',
      accent: '45 70% 92%', 'accent-foreground': '268 50% 30%',
      ring: '45 90% 50%', background: '268 30% 98%',
    },
  },
  valentines: {
    label: "Valentine's / Family",
    intensity: 'subtle',
    accent_effect: 'hearts',
    ambient: 'rose',
    swatch: '#d6436e',
    tokens: {
      brand: '345 75% 52%', 'brand-light': '345 80% 64%', 'brand-dark': '345 70% 42%',
      primary: '345 70% 44%', 'primary-foreground': '0 0% 100%',
      accent: '345 60% 95%', 'accent-foreground': '345 65% 36%',
      ring: '345 75% 52%', background: '345 45% 99%',
    },
  },
  dr_independence: {
    label: 'Dominican Republic',
    intensity: 'subtle',
    accent_effect: 'none',
    ambient: 'beams',
    flags: ['DR'],
    swatch: '#002d62',
    tokens: {
      brand: '215 90% 32%', 'brand-light': '215 85% 46%', 'brand-dark': '215 95% 24%',
      primary: '352 80% 44%', 'primary-foreground': '0 0% 100%',
      accent: '215 50% 94%', 'accent-foreground': '215 90% 28%',
      ring: '215 90% 32%', background: '215 40% 98%',
    },
  },
  mothers_day: {
    label: "Mother's Day",
    intensity: 'subtle',
    accent_effect: 'petals',
    ambient: 'bloom',
    swatch: '#d85c92',
    tokens: {
      brand: '335 65% 55%', 'brand-light': '335 70% 66%', 'brand-dark': '335 60% 44%',
      primary: '335 55% 46%', 'primary-foreground': '0 0% 100%',
      accent: '335 55% 95%', 'accent-foreground': '335 55% 36%',
      ring: '335 65% 55%', background: '335 40% 99%',
    },
  },
  fathers_day: {
    label: "Father's Day",
    intensity: 'subtle',
    accent_effect: 'none',
    ambient: 'gold-night',
    swatch: '#2b3a55',
    tokens: {
      brand: '45 80% 48%', 'brand-light': '46 85% 60%', 'brand-dark': '40 75% 40%',
      primary: '220 40% 24%', 'primary-foreground': '45 82% 90%',
      accent: '220 25% 93%', 'accent-foreground': '220 40% 24%',
      ring: '45 80% 48%', background: '220 25% 98%',
    },
  },
  argentina_independence: {
    label: 'Argentina',
    intensity: 'subtle',
    accent_effect: 'sparkle',
    ambient: 'beams',
    flags: ['AR'],
    swatch: '#74acdf',
    tokens: {
      brand: '205 62% 52%', 'brand-light': '205 68% 64%', 'brand-dark': '208 60% 42%',
      primary: '205 60% 38%', 'primary-foreground': '0 0% 100%',
      accent: '205 55% 95%', 'accent-foreground': '205 60% 30%',
      ring: '205 62% 52%', background: '205 45% 99%',
    },
  },
  colombia_independence: {
    label: 'Colombia',
    intensity: 'subtle',
    accent_effect: 'none',
    ambient: 'beams',
    flags: ['CO'],
    swatch: '#fcd116',
    tokens: {
      brand: '48 95% 50%', 'brand-light': '48 98% 62%', 'brand-dark': '44 90% 42%',
      primary: '222 100% 30%', 'primary-foreground': '0 0% 100%',
      accent: '48 85% 92%', 'accent-foreground': '222 80% 28%',
      ring: '48 95% 50%', background: '48 50% 98%',
    },
  },
  mexico_independence: {
    label: 'Mexico',
    intensity: 'subtle',
    accent_effect: 'none',
    ambient: 'beams',
    flags: ['MX'],
    swatch: '#006847',
    tokens: {
      brand: '162 90% 24%', 'brand-light': '160 70% 34%', 'brand-dark': '164 95% 18%',
      primary: '162 85% 22%', 'primary-foreground': '0 0% 100%',
      accent: '352 60% 94%', 'accent-foreground': '162 85% 22%',
      ring: '352 75% 46%', background: '162 35% 98%',
    },
  },
  chile_independence: {
    label: 'Chile',
    intensity: 'subtle',
    accent_effect: 'sparkle',
    ambient: 'beams',
    flags: ['CL'],
    swatch: '#0039a6',
    tokens: {
      brand: '222 100% 33%', 'brand-light': '222 85% 48%', 'brand-dark': '222 100% 26%',
      primary: '222 90% 34%', 'primary-foreground': '0 0% 100%',
      accent: '222 45% 94%', 'accent-foreground': '222 90% 28%',
      ring: '0 78% 48%', background: '222 40% 98%',
    },
  },
  puerto_rico: {
    label: 'Puerto Rico',
    intensity: 'subtle',
    accent_effect: 'sparkle',
    ambient: 'beams',
    flags: ['PR'],
    swatch: '#0050f0',
    tokens: {
      brand: '222 100% 47%', 'brand-light': '222 90% 60%', 'brand-dark': '222 100% 38%',
      primary: '356 85% 46%', 'primary-foreground': '0 0% 100%',
      accent: '222 55% 94%', 'accent-foreground': '222 90% 34%',
      ring: '222 100% 47%', background: '222 40% 98%',
    },
  },

  // ─── BASE SEASONS (subtle) ──────────────────────────────────────────
  spring: {
    label: 'Spring',
    intensity: 'subtle',
    accent_effect: 'petals',
    ambient: 'bloom',
    swatch: '#3fae63',
    tokens: {
      brand: '142 52% 40%', 'brand-light': '142 58% 52%', 'brand-dark': '148 55% 32%',
      primary: '150 42% 24%', 'primary-foreground': '140 70% 97%',
      accent: '142 46% 94%', 'accent-foreground': '150 50% 24%',
      ring: '142 52% 40%', background: '135 42% 98%',
    },
  },
  summer: {
    label: 'Summer',
    intensity: 'subtle',
    accent_effect: 'none',
    ambient: 'sun',
    swatch: '#10a7c4',
    tokens: {
      brand: '190 82% 40%', 'brand-light': '190 85% 52%', 'brand-dark': '192 80% 33%',
      primary: '195 70% 28%', 'primary-foreground': '190 70% 97%',
      accent: '190 66% 94%', 'accent-foreground': '195 75% 24%',
      ring: '190 82% 40%', background: '190 55% 98%',
    },
  },
  fall: {
    label: 'Fall / Autumn',
    intensity: 'subtle',
    accent_effect: 'leaves',
    ambient: 'autumn',
    swatch: '#e07a1f',
    tokens: {
      brand: '25 80% 46%', 'brand-light': '30 85% 56%', 'brand-dark': '18 75% 38%',
      primary: '22 55% 24%', 'primary-foreground': '36 80% 96%',
      accent: '28 66% 93%', 'accent-foreground': '22 60% 24%',
      ring: '25 80% 46%', background: '30 55% 97%',
    },
  },
  winter: {
    label: 'Winter',
    intensity: 'subtle',
    accent_effect: 'snow',
    ambient: 'frost',
    swatch: '#2f7fd1',
    tokens: {
      brand: '205 75% 46%', 'brand-light': '202 82% 58%', 'brand-dark': '210 75% 38%',
      primary: '212 55% 26%', 'primary-foreground': '205 80% 97%',
      accent: '205 56% 95%', 'accent-foreground': '212 55% 26%',
      ring: '205 75% 46%', background: '205 45% 98%',
    },
  },

  // ─── RETAINED ───────────────────────────────────────────────────────
  thanksgiving: {
    label: 'Thanksgiving',
    intensity: 'subtle',
    accent_effect: 'leaves',
    ambient: 'autumn',
    swatch: '#b5531e',
    tokens: {
      brand: '18 78% 46%', 'brand-light': '24 82% 56%', 'brand-dark': '14 72% 38%',
      primary: '20 58% 20%', 'primary-foreground': '32 75% 96%',
      accent: '26 65% 92%', 'accent-foreground': '20 60% 22%',
      ring: '18 78% 46%', background: '28 48% 97%',
      'sidebar-bg': '20 45% 13%', 'sidebar-active': '36 85% 58%',
    },
  },
};

export const EFFECT_OPTIONS = [
  { value: 'none', label: 'No particles' },
  { value: 'leaves', label: 'Falling leaves' },
  { value: 'snow', label: 'Snow' },
  { value: 'petals', label: 'Petals' },
  { value: 'sparkle', label: 'Sparkles' },
  { value: 'hearts', label: 'Hearts' },
];

// ── Custom-theme helpers ────────────────────────────────────────────────
// Convert a #rrggbb hex to an "H S% L%" triplet string (index.css format).
export function hexToHslTriplet(hex) {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return '199 89% 48%';
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const lum = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = lum > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(lum * 100)}%`;
}

// Derive a full, legibility-safe token set from a single brand hex color.
export function deriveCustomTokens(hex) {
  const parts = hexToHslTriplet(hex).split(' ');
  const H = parseInt(parts[0], 10) || 199;
  const S = parseInt(parts[1], 10) || 80;
  const clamp = (n) => Math.max(0, Math.min(100, n));
  const base = {
    brand: `${H} ${clamp(S)}% 48%`,
    'brand-light': `${H} ${clamp(S + 4)}% 60%`,
    'brand-dark': `${H} ${clamp(S)}% 38%`,
    primary: `${H} ${clamp(S - 10)}% 22%`,
    'primary-foreground': `${H} 70% 97%`,
    accent: `${H} 55% 93%`,
    'accent-foreground': `${H} ${clamp(S - 5)}% 24%`,
    ring: `${H} ${clamp(S)}% 48%`,
    background: `${H} 40% 98%`,
  };
  return { ...base, ...krChromeFromTokens(base) };
}

// ── Portal chrome derivation ────────────────────────────────────────────
// The KR portal chrome (sidebar, gold accents, page canvas, borders, DS stat
// cards, pipeline columns) is painted from a family of `--kr-*` CSS variables
// defined in App.css — NOT from the shadcn tokens above. To make a theme feel
// truly immersive (coloured sidebar / icons / columns / buttons), each preset
// must also override those `--kr-*` variables. We derive a cohesive chrome
// ramp from the preset's own `primary` (deep chrome hue) and `brand` (signature
// accent hue) so every theme — including Custom — transforms the entire portal.
function parseHsl(triplet) {
  const [h, s, l] = String(triplet || '').replace(/%/g, '').trim().split(/\s+/).map(Number);
  return { h: h || 0, s: Number.isFinite(s) ? s : 40, l: Number.isFinite(l) ? l : 40 };
}
const _c = (h, s, l) => `hsl(${Math.round(h)} ${Math.round(Math.max(0, Math.min(100, s)))}% ${Math.round(Math.max(0, Math.min(100, l)))}%)`;

function krChromeFromTokens(tk) {
  const p = parseHsl(tk.primary || '222 47% 16%');
  const b = parseHsl(tk.brand || '45 90% 48%');
  const H = p.h;
  const S = Math.min(Math.max(p.s, 30), 60); // rich, never neon
  const bH = b.h;
  const bS = b.s;
  const bL = b.l;
  return {
    // Deep chrome ramp → sidebar, dark surfaces, KPI values
    'kr-navy-950': _c(H, S, 8),
    'kr-navy-900': _c(H, S, 12),
    'kr-navy-800': _c(H, S - 2, 17),
    'kr-navy-700': _c(H, S - 4, 26),
    'kr-navy-600': _c(H, S - 6, 34),
    'kr-navy-500': _c(H, S - 8, 44),
    'kr-navy-100': _c(H, 42, 90),
    'kr-navy-50': _c(H, 46, 96),
    // Signature accent ramp → active nav indicator, gold chips, highlights
    'kr-gold-600': _c(bH, Math.min(bS + 6, 95), Math.max(bL - 10, 28)),
    'kr-gold-500': _c(bH, bS, bL),
    'kr-gold-400': _c(bH, bS, Math.min(bL + 10, 66)),
    'kr-gold-100': _c(bH, Math.min(bS, 82), 90),
    'kr-gold-50': _c(bH, Math.min(bS, 82), 96),
    // Workspace surfaces
    'kr-canvas': _c(bH, 34, 97),
    'kr-border': _c(H, 24, 90),
    'kr-border-strong': _c(H, 22, 82),
    'kr-surface': '#ffffff',
    'kr-text': _c(H, 42, 15),
    'kr-muted': _c(H, 20, 42),
    'kr-subtle': _c(H, 16, 56),
  };
}

// Atmospheric portrait artwork painted behind the sidebar (under a dark,
// season-tinted overlay so navigation stays perfectly legible). Strong themes
// reuse their hero art; base seasons use dedicated vertical imagery.
export const SIDEBAR_IMAGES = {
  spring: `${HERO_BASE}/9a9f1f6e08feb923ae547641310fc35705082e2183505a7156be00a0683e951d.jpeg`,
  summer: `${HERO_BASE}/75282bd604d09b72e6ffe7343646cbb522cca092a078bda136eb331531758f2a.jpeg`,
  fall: `${HERO_BASE}/385c2fc5a16f8e4851bd17a2cc2b7ea9e9a695f5aed74f29058a7089ddc01025.jpeg`,
  thanksgiving: `${HERO_BASE}/57609cc17fdc469e503ab362ea3208b6353c6dde49e07f63bd3552734fbf2a1a.jpeg`,
  winter: `${HERO_BASE}/841b9ac65fd3807fb0db1de05cde791a540ec4c95d6e6de8a433337153811f29.jpeg`,
};

// Curated gallery the admin can pick from in the editor (season-agnostic).
export const SIDEBAR_IMAGE_CHOICES = [
  { key: 'spring', label: 'Spring blossom', url: SIDEBAR_IMAGES.spring },
  { key: 'summer', label: 'Summer greens', url: SIDEBAR_IMAGES.summer },
  { key: 'fall', label: 'Autumn forest', url: SIDEBAR_IMAGES.fall },
  { key: 'winter', label: 'Winter pines', url: SIDEBAR_IMAGES.winter },
  { key: 'thanksgiving', label: 'Harvest', url: SIDEBAR_IMAGES.thanksgiving },
];

// Default image-visibility (opacity of the sidebar artwork under the scrim).
export const DEFAULT_SIDEBAR_INTENSITY = 0.42;

export function presetSidebarImage(template) {
  return SIDEBAR_IMAGES[template] || SEASONAL_PRESETS[template]?.hero || null;
}

export function presetTokens(template) {
  const base = SEASONAL_PRESETS[template]?.tokens;
  if (!base) return {};
  const img = presetSidebarImage(template);
  return {
    ...base,
    ...krChromeFromTokens(base),
    'kr-sidebar-image': img ? `url("${img}")` : 'none',
    'kr-sidebar-image-opacity': String(DEFAULT_SIDEBAR_INTENSITY),
  };
}
export function presetEffect(template) {
  return SEASONAL_PRESETS[template]?.accent_effect || 'none';
}
export function presetAmbient(template) {
  return SEASONAL_PRESETS[template]?.ambient || 'none';
}
export function presetFlags(template) {
  return SEASONAL_PRESETS[template]?.flags || null;
}
export function presetHero(template) {
  return SEASONAL_PRESETS[template]?.hero || null;
}
export function presetIntensity(template) {
  return SEASONAL_PRESETS[template]?.intensity || 'subtle';
}
